import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { Helmet } from 'react-helmet-async'
import {
    FiArrowRight,
    FiBriefcase,
    FiCheckCircle,
    FiClock,
    FiDollarSign,
    FiGlobe,
    FiMessageSquare,
    FiShield,
    FiTrendingUp,
    FiUsers
} from 'react-icons/fi'
import { useGetPlatformStatsQuery } from '@/services/statsApi'
import HeroSearchBar from '@/components/ui/HeroSearchBar'
import Logo from '@/components/ui/Logo'

const features = [
    {
        title: 'Global Reach',
        description: 'Work with clients and talent from every region with built-in localization support.',
        icon: FiGlobe,
        iconStyles: 'bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300'
    },
    {
        title: 'Secure Payments',
        description: 'Reliable payouts and payment protection with transparent fee handling.',
        icon: FiShield,
        iconStyles: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300'
    },
    {
        title: 'Faster Hiring',
        description: 'Post gigs, receive proposals, and make decisions in a streamlined flow.',
        icon: FiClock,
        iconStyles: 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300'
    },
    {
        title: 'Real-time Chat',
        description: 'Collaborate instantly with smart conversation tools and better context.',
        icon: FiMessageSquare,
        iconStyles: 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300'
    },
    {
        title: 'Trust & Verification',
        description: 'Verified profiles and reputation signals help reduce risk and improve outcomes.',
        icon: FiCheckCircle,
        iconStyles: 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'
    },
    {
        title: 'Smart Earnings',
        description: 'Track your growth and optimize your work with performance insights.',
        icon: FiDollarSign,
        iconStyles: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-300'
    }
]

const HomePage: React.FC = () => {
    const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
    const { data: platformStats, isLoading: isLoadingStats, error: statsError } = useGetPlatformStatsQuery()

    const totalGigs = platformStats?.data?.totalGigs ?? 1000
    const totalUsers = platformStats?.data?.totalUsers ?? 5000
    const successRate = platformStats?.data?.successRate ?? 95

    return (
        <>
            <Helmet>
                <title>SideGO - Global Gig Marketplace</title>
                <meta
                    name="description"
                    content="Find side hustles, freelance work, and gig opportunities worldwide. Connect with employers globally and build your career on your terms."
                />
                <meta name="keywords" content="gigs, side hustle, freelance, jobs, global, work, opportunities, worldwide" />
            </Helmet>

            <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-gray-950 dark:text-gray-100">
                <section className="relative overflow-hidden px-4 pt-10 pb-12 sm:px-6 sm:pt-14 sm:pb-16 lg:px-8 lg:pt-16">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-500/20" />
                        <div className="absolute top-10 right-0 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl dark:bg-blue-500/20" />
                        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-500/10" />
                    </div>

                    <div className="relative mx-auto grid max-w-7xl gap-8 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <div className="mb-6 flex items-center gap-3">
                                <Logo
                                    size="xl"
                                    showText={false}
                                    variant="default"
                                    backgroundColor="bg-transparent"
                                    className="scale-[1.35] transition-transform duration-200 hover:scale-[1.45]"
                                />
                                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/70 dark:text-gray-300">
                                    Global Gig Marketplace
                                </span>
                            </div>

                            <h1 className="mb-4 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
                                Find premium gigs and trusted talent in one{' '}
                                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent dark:from-cyan-400 dark:to-blue-400">
                                    modern workspace
                                </span>
                            </h1>

                            <p className="mb-6 max-w-2xl text-base text-slate-700 sm:text-lg dark:text-gray-300">
                                SideGO helps workers and employers connect faster with verified profiles, clear communication,
                                and secure transactions designed for global collaboration.
                            </p>

                            <div className="mb-6 flex flex-wrap gap-2 sm:gap-3">
                                <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                                    Verified Community
                                </span>
                                <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                                    Multi-Currency Support
                                </span>
                                <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                                    Real-time Collaboration
                                </span>
                            </div>

                            <div className="mb-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
                                {!isAuthenticated ? (
                                    <>
                                        <Link
                                            to="/register?role=worker"
                                            className="btn-hero w-full text-sm sm:w-auto sm:text-base"
                                        >
                                            Find Work
                                            <FiArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                                        </Link>
                                        <Link
                                            to="/register?role=employer"
                                            className="inline-flex w-full items-center justify-center rounded-xl border-2 border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-100 hover:shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-500 dark:hover:bg-gray-800 sm:w-auto sm:px-8 sm:py-4 sm:text-base"
                                        >
                                            Hire Talent
                                            <FiArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                                        </Link>
                                    </>
                                ) : (
                                    <Link
                                        to={user?.role === 'worker' ? '/gigs' : '/dashboard/gigs/create'}
                                        className="btn-hero w-full text-sm sm:w-auto sm:text-base"
                                    >
                                        {user?.role === 'worker' ? 'Browse Gigs' : 'Post a Gig'}
                                        <FiArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                                    </Link>
                                )}
                            </div>

                            <div className="max-w-2xl">
                                <HeroSearchBar placeholder="Search for gigs, skills, or services..." />
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="rounded-3xl border border-slate-200 bg-white/85 p-5 shadow-xl backdrop-blur dark:border-gray-700 dark:bg-gray-900/80 sm:p-6">
                                <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                                    Marketplace Highlights
                                </p>

                                <div className="space-y-3">
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                                        <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Weekly Active Gigs</p>
                                        <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-gray-100">
                                            {isLoadingStats ? 'Loading...' : `${totalGigs.toLocaleString()}+`}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                                            <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Users</p>
                                            <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-gray-100">
                                                {isLoadingStats ? '...' : `${totalUsers.toLocaleString()}+`}
                                            </p>
                                        </div>
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                                            <p className="text-xs font-medium text-slate-500 dark:text-gray-400">Success Rate</p>
                                            <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-gray-100">
                                                {isLoadingStats ? '...' : `${successRate}%`}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-900/30">
                                        <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Fast onboarding</p>
                                        <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-400">
                                            Create your profile, publish your first gig, and start connecting in minutes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="px-4 pb-10 sm:px-6 sm:pb-12 lg:px-8">
                    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900">
                            <div className="mb-2 inline-flex rounded-xl bg-cyan-100 p-2.5 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-300">
                                <FiBriefcase className="h-4.5 w-4.5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 sm:text-2xl">
                                {isLoadingStats ? '...' : `${totalGigs.toLocaleString()}+`}
                            </h3>
                            <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">Active gigs open now</p>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900">
                            <div className="mb-2 inline-flex rounded-xl bg-emerald-100 p-2.5 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <FiUsers className="h-4.5 w-4.5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 sm:text-2xl">
                                {isLoadingStats ? '...' : `${totalUsers.toLocaleString()}+`}
                            </h3>
                            <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">Registered users worldwide</p>
                        </div>

                        <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 lg:col-span-1">
                            <div className="mb-2 inline-flex rounded-xl bg-amber-100 p-2.5 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
                                <FiTrendingUp className="h-4.5 w-4.5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 sm:text-2xl">
                                {isLoadingStats ? '...' : `${successRate}%`}
                            </h3>
                            <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">Successful project completion rate</p>
                        </div>
                    </div>
                </section>

                <section className="px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
                    <div className="mx-auto max-w-7xl">
                        <div className="mb-8 flex flex-col gap-3 sm:mb-10">
                            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700 dark:text-cyan-300">
                                Built For Speed
                            </span>
                            <h2 className="text-3xl font-bold text-slate-900 dark:text-gray-100 sm:text-4xl">
                                A compact workflow from discovery to delivery
                            </h2>
                            <p className="max-w-3xl text-base text-slate-600 dark:text-gray-400 sm:text-lg">
                                Everything you need to post, discover, communicate, and complete work is centralized so
                                projects move faster with fewer steps.
                            </p>
                        </div>

                        <div className="mb-8 grid gap-5 sm:grid-cols-3 sm:gap-6">
                            {[
                                {
                                    step: '01',
                                    title: 'Create your profile',
                                    text: 'Set your role, add core details, and become discoverable instantly.'
                                },
                                {
                                    step: '02',
                                    title: 'Match and connect',
                                    text: 'Search, filter, and message directly with the right people.'
                                },
                                {
                                    step: '03',
                                    title: 'Deliver with confidence',
                                    text: 'Track progress, complete gigs, and grow with ratings and insights.'
                                }
                            ].map((item) => (
                                <div
                                    key={item.step}
                                    className="relative rounded-2xl border border-slate-200 bg-white p-5 pt-8 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                                >
                                    <div className="absolute left-1/2 top-0 inline-flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 text-sm font-bold text-cyan-700 shadow-md dark:border-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-200">
                                        {item.step}
                                    </div>
                                    <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-gray-100">{item.title}</h3>
                                    <p className="mt-2 text-sm text-slate-600 dark:text-gray-400">{item.text}</p>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {features.map((feature) => {
                                const Icon = feature.icon

                                return (
                                    <div
                                        key={feature.title}
                                        className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900"
                                    >
                                        <div className="mb-4 flex items-center gap-3">
                                            <div className={`inline-flex rounded-xl p-3 ${feature.iconStyles}`}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100">{feature.title}</h3>
                                        </div>
                                        <p className="text-sm text-slate-600 dark:text-gray-400">
                                            {feature.description}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </section>

                <section className="px-4 pb-10 sm:px-6 sm:pb-16 lg:px-8">
                    <div className="mx-auto max-w-5xl">
                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-cyan-900 to-blue-900 px-4 py-6 text-center shadow-2xl dark:border-gray-700 sm:rounded-3xl sm:px-10 sm:py-10">
                            <div className="pointer-events-none absolute -top-20 right-10 h-44 w-44 rounded-full bg-cyan-300/30 blur-3xl" />
                            <div className="pointer-events-none absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-blue-300/20 blur-3xl" />

                            <h2 className="relative mx-auto max-w-md text-2xl font-bold leading-tight text-white sm:max-w-3xl sm:text-4xl">
                                Ready to grow your next project on SideGO?
                            </h2>
                            <p className="relative mx-auto mt-2 max-w-sm text-sm leading-relaxed text-cyan-100 sm:mt-3 sm:max-w-2xl sm:text-base">
                                Join a global network where hiring and earning feel effortless, transparent, and faster than ever.
                            </p>

                            {!isAuthenticated && (
                                <div className="relative mt-5 flex flex-col items-center justify-center gap-2.5 sm:mt-6 sm:flex-row sm:gap-4">
                                    <Link
                                        to="/register"
                                        className="inline-flex w-full items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-100 sm:w-auto sm:px-6 sm:py-3"
                                    >
                                        Sign Up Free
                                        <FiArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/gigs"
                                        className="inline-flex w-full items-center justify-center rounded-xl border border-white/50 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/20 sm:w-auto sm:px-6 sm:py-3"
                                    >
                                        Browse Opportunities
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default HomePage